/**
 * CopyMessageButton — the per-message copy affordance for a settled assistant
 * turn, sized for the hover-revealed actions rail beside `MessageRow`.
 *
 * Writes BOTH clipboard flavours when there is rich text to write, which is the
 * reason this exists rather than a bare `useCopyToClipboard()` call. That hook
 * (and `navigator.clipboard.writeText`) can only ever produce `text/plain`, so a
 * copied markdown link pastes into a document as the literal characters
 * `[title](https://…)` instead of a hyperlink. Pass `html` and the button writes
 * a `ClipboardItem` carrying `text/plain` + `text/html`, so a paste into a doc,
 * an email, or a rich composer arrives formatted the way the answer looks on
 * screen. A paste target picks `text/html` when it can and falls back to
 * `text/plain` itself, so the plain string is never lost.
 *
 * `html` is a THUNK, not a string, and that is load-bearing: rendering an answer
 * to HTML is real work, most messages are never copied, and a transcript
 * re-renders on every streamed frame. The thunk runs on click and nowhere else.
 *
 * Every rich-path failure degrades to the plain write rather than to an error —
 * no `ClipboardItem` (older Safari, jsdom), a rejected multi-flavour write, or a
 * renderer fault returning null. Losing formatting is a disappointment; losing
 * the copy is a bug. Only a failure of BOTH paths calls `onError`.
 *
 * The Copy -> Check swap IS the success signal, so nothing is announced: a toast
 * on every copy is noise when the button is already sitting under the cursor.
 * The swap is an instant conditional render with a colour-only transition, so it
 * stays calm under reduced motion and animates no layout.
 *
 * No built-in tooltip, per the `ActionIconTile` precedent: the button carries a
 * real `aria-label` and the consumer pairs it with their own tooltip, so label
 * and hint stay one string owned in one place.
 *
 *   <MessageRow role="assistant" actions={
 *     <CopyMessageButton text={m.text} html={() => toHtml(m)} onError={notify.error} />
 *   }>…</MessageRow>
 */
import * as React from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/cn";
import { Spinner } from "../primitives/Spinner";

/** How long the Check stays up before reverting to Copy. */
const COPIED_RESET_MS = 1500;

export interface CopyMessageButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onError" | "children"
> {
  /** The plain text to copy — the markdown/plain the row rendered, never HTML. */
  text: string;
  /**
   * The rich flavour, built on demand. Returning `null` (or omitting this)
   * writes plain text only.
   */
  html?: () => string | null;
  /** Fired when BOTH clipboard paths fail. The consumer owns the toast. */
  onError?: (error: unknown) => void;
  /** Fired after a successful write, with the flavours actually written. */
  onCopied?: (flavours: { plain: true; html: boolean }) => void;
  /** Accessible name at rest. Default `"Copy message"`. */
  copyLabel?: string;
  /** Accessible name while the check is up. Default `"Copied"`. */
  copiedLabel?: string;
  /**
   * A copy is in flight: swaps the icon for a `<Spinner>` and force-disables.
   * Most copies are instant and need this never; it exists for the case the
   * `html` thunk is genuinely expensive (rendering a long answer), where the
   * button would otherwise sit dead under the cursor with no signal.
   */
  isCopying?: boolean;
}

export const CopyMessageButton = React.forwardRef<HTMLButtonElement, CopyMessageButtonProps>(
  function CopyMessageButton(
    {
      text,
      html,
      onError,
      onCopied,
      copyLabel = "Copy message",
      copiedLabel = "Copied",
      isCopying = false,
      className,
      onClick,
      ...props
    },
    ref,
  ) {
    const [copied, setCopied] = React.useState(false);
    /*
     * The pending reset, ref-held so a re-copy inside the window extends the check
     * instead of racing it back early, and so unmount never leaves a timer firing
     * setState on a dead component.
     */
    const resetTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
    React.useEffect(() => () => clearTimeout(resetTimer.current), []);

    const onCopy = async () => {
      let wroteHtml = false;
      try {
        const rich = html?.() ?? null;
        if (
          rich &&
          typeof ClipboardItem === "function" &&
          typeof navigator.clipboard?.write === "function"
        ) {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({
                "text/plain": new Blob([text], { type: "text/plain" }),
                "text/html": new Blob([rich], { type: "text/html" }),
              }),
            ]);
            wroteHtml = true;
          } catch {
            /* fall through to the plain write below */
          }
        }
        if (!wroteHtml) await navigator.clipboard.writeText(text);

        setCopied(true);
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
        onCopied?.({ plain: true, html: wroteHtml });
      } catch (error) {
        onError?.(error);
      }
    };

    const label = copied ? copiedLabel : copyLabel;

    /*
     * COMPOSED, and `{...props}` is spread BEFORE it. Both halves are
     * load-bearing: a Radix `asChild` trigger (Tooltip, Popover) clones this
     * element and injects its OWN onClick, so a trailing spread silently replaces
     * the copy handler and the button renders perfectly while doing nothing.
     * Spread first, then compose, so the parent's handler runs and so does ours.
     */
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      void onCopy();
    };

    return (
      <button
        ref={ref}
        type="button"
        {...props}
        onClick={handleClick}
        disabled={props.disabled || isCopying}
        aria-busy={isCopying || undefined}
        aria-label={label}
        /*
         * Rests muted and gains a BACKGROUND on hover, not just a darker glyph:
         * an actions rail that no longer fades in as a whole has stopped being
         * the "you are pointing at this" signal, so each control carries that
         * state itself. focus-visible mirrors it so the keyboard sees what the
         * mouse does.
         */
        className={cn(
          "inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground",
          "transition-colors hover:bg-muted hover:text-foreground",
          "focus-visible:bg-muted focus-visible:text-foreground focus-visible:outline-none",
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
      >
        {isCopying ? (
          <Spinner className="h-3.5 w-3.5" decorative />
        ) : copied ? (
          <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    );
  },
);
